import type React from 'react'
import { ChevronDown, ChevronUp } from 'lucide-react'
import { Button } from '@components/ui/button'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@components/ui/command'
import { Popover, PopoverContent, PopoverTrigger } from '@components/ui/popover'
import type { FontFamily } from '@/api/types/font-family'
import { keepTextEditFocus } from '../handlers/keep-text-edit-focus'

interface FontFamilyPickerProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onTriggerKeyDown: (e: React.KeyboardEvent) => void
  onOpenAutoFocus: (e: Event) => void
  viewportRef: React.Ref<HTMLDivElement>
  onListScroll: () => void
  disabled: boolean
  isUsedCatalogLoading: boolean
  hasTextSelectionTarget: boolean
  fontFamilyIdMixed: boolean
  currentFontDisplayName: string | null
  allFamiliesSorted: FontFamily[]
  failedFontFamilyNames: ReadonlySet<string>
  defaultFontFamilyName: string | null
  onSelectDefault: () => void
  onSelectFamily: (fontFamilyId: number, isFontFailed: boolean) => void
  onFamilyFocus: (fontFamilyId: number) => void
}

interface TriggerState {
  isUsedCatalogLoading: boolean
  fontFamilyIdMixed: boolean
  currentFontDisplayName: string | null
  hasTextSelectionTarget: boolean
  allFamiliesSorted: FontFamily[]
}

function resolveTriggerAriaLabel({
  isUsedCatalogLoading,
  fontFamilyIdMixed,
  currentFontDisplayName,
}: TriggerState): string {
  if (isUsedCatalogLoading) return 'Font Family, loading'
  if (fontFamilyIdMixed) return 'Font Family: Mixed'

  return currentFontDisplayName ? `Font Family: ${currentFontDisplayName}` : 'Font Family'
}

function resolveTriggerText({
  isUsedCatalogLoading,
  fontFamilyIdMixed,
  currentFontDisplayName,
  hasTextSelectionTarget,
  allFamiliesSorted,
}: TriggerState): string {
  if (fontFamilyIdMixed) return 'Mixed'
  if (currentFontDisplayName != null) return currentFontDisplayName
  if (isUsedCatalogLoading) return 'Loading fonts…'
  if (!hasTextSelectionTarget) return 'Add a text box to choose fonts'

  // When the font catalog hasn't loaded (API blocked or unavailable), fall back to the system default name.
  return allFamiliesSorted.length === 0 && !isUsedCatalogLoading ? 'Helvetica' : 'Font family'
}

/** The Font Family popover: trigger, pinned default-font row, and the searchable family list. */
export function FontFamilyPicker({
  open,
  onOpenChange,
  onTriggerKeyDown,
  onOpenAutoFocus,
  viewportRef,
  onListScroll,
  disabled,
  isUsedCatalogLoading,
  hasTextSelectionTarget,
  fontFamilyIdMixed,
  currentFontDisplayName,
  allFamiliesSorted,
  failedFontFamilyNames,
  defaultFontFamilyName,
  onSelectDefault,
  onSelectFamily,
  onFamilyFocus,
}: FontFamilyPickerProps) {
  const triggerState = {
    isUsedCatalogLoading,
    fontFamilyIdMixed,
    currentFontDisplayName,
    hasTextSelectionTarget,
    allFamiliesSorted,
  }

  return (
    <div className="mt-2 space-y-1" onPointerDown={keepTextEditFocus}>
      <Popover open={open} onOpenChange={onOpenChange}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            aria-haspopup="listbox"
            aria-expanded={open}
            onKeyDown={onTriggerKeyDown}
            aria-label={resolveTriggerAriaLabel(triggerState)}
            aria-busy={isUsedCatalogLoading}
            className="w-full justify-between h-8 text-sm font-normal"
            disabled={disabled || isUsedCatalogLoading || !hasTextSelectionTarget}
          >
            <span className="truncate" aria-hidden="true">
              {resolveTriggerText(triggerState)}
            </span>
            {open ? (
              <ChevronUp className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            ) : (
              <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="w-[var(--radix-popover-trigger-width)] p-0"
          align="start"
          aria-label="Font family picker"
          data-text-edit-keepalive=""
          onOpenAutoFocus={onOpenAutoFocus}
        >
          <Command label="Search font families">
            <CommandInput placeholder="Search" />
            {/* Default font pinned above the scrollable list — not filtered by search */}
            <div className="border-b border-border px-1 py-1">
              <div
                role="option"
                className="relative flex cursor-pointer select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none hover:bg-accent hover:text-accent-foreground"
                onClick={onSelectDefault}
              >
                {defaultFontFamilyName ?? 'Default Font'}
              </div>
            </div>
            <CommandList ref={viewportRef} label="Font family options" onScroll={onListScroll}>
              <CommandEmpty>No font found.</CommandEmpty>
              <CommandGroup>
                {allFamiliesSorted.map((fam) => {
                  const isFontFailed = failedFontFamilyNames.has(fam.familyName)

                  return (
                    <CommandItem
                      key={fam.fontFamilyId}
                      value={fam.displayName}
                      disabled={isFontFailed}
                      onSelect={() => onSelectFamily(fam.fontFamilyId, isFontFailed)}
                      className="text-sm justify-start"
                      style={
                        isFontFailed ? undefined : { fontFamily: `"${fam.familyName}", sans-serif` }
                      }
                      data-lazy-font-family={fam.fontFamilyId}
                      onFocus={() => !isFontFailed && onFamilyFocus(fam.fontFamilyId)}
                    >
                      {fam.displayName}
                    </CommandItem>
                  )
                })}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  )
}
